import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import type { Agent } from '@/api/agentTypes';
import { markBackofficeUnauthorized } from '@/api/backofficeAuth';
import { applyHubAccessHeader } from '@/api/hubAccessHeaders';
import type { Hospital } from '@/api/hospitalTypes';
import type {
    CreateOrgTokenRequest,
    CreateOrgTokenResponse,
    OrgTokenListItem,
} from '@/api/orgTokenTypes';
import type { ProtocolListItem } from '@/api/protocolTypes';
import { HUB_API_URL } from '@/config/env';

// The backoffice credential travels as the httpOnly cookie POST
// /api/v1/backoffice/login sets (see backofficeAuth.ts) — the browser
// attaches it on its own. `fetchBaseQuery` is fetch-based, so it needs
// `credentials: 'include'` spelled out: unlike axios's `withCredentials`,
// fetch defaults to same-origin and would silently drop the cookie on this
// cross-origin call to the hub.
const rawBaseQuery = fetchBaseQuery({
    baseUrl: HUB_API_URL,
    credentials: 'include',
    prepareHeaders: applyHubAccessHeader,
});

// A 401 from any backoffice-gated query means the cookie is no good, however
// connected the UI still thinks it is — bounce back to the login page rather
// than leaving a dashboard up that can't load anything (see
// markBackofficeUnauthorized's own note for how that happens).
const baseQueryWithUnauthorizedGate: typeof rawBaseQuery = async (args, api, extraOptions) => {
    const result = await rawBaseQuery(args, api, extraOptions);
    if (result.error?.status === 401) markBackofficeUnauthorized();
    return result;
};

export const hubApi = createApi({
    reducerPath: 'hubApi',
    baseQuery: baseQueryWithUnauthorizedGate,
    tagTypes: ['OrgToken'],
    endpoints: (builder) => ({
        listOrgTokens: builder.query<OrgTokenListItem[], void>({
            query: () => '/api/v1/org-tokens',
            providesTags: ['OrgToken'],
        }),
        createOrgToken: builder.mutation<CreateOrgTokenResponse, CreateOrgTokenRequest>({
            query: (body) => ({
                url: '/api/v1/org-tokens',
                method: 'POST',
                body,
            }),
            invalidatesTags: ['OrgToken'],
        }),
        listHospitals: builder.query<Hospital[], void>({
            query: () => '/api/v1/hospitals',
        }),
        listAgents: builder.query<Agent[], void>({
            query: () => '/api/v1/agents',
        }),
        listProtocols: builder.query<ProtocolListItem[], void>({
            query: () => '/api/v1/protocols',
        }),
    }),
});

export const {
    useListOrgTokensQuery,
    useCreateOrgTokenMutation,
    useListHospitalsQuery,
    useListAgentsQuery,
    useListProtocolsQuery,
} = hubApi;
